/**
 * Canonical ImportJob data model
 *
 * An ImportJob records a single import: who ran it, when, where its content came
 * from (a CSV upload or a set of PDFs) and the id of every question it created for
 * audit. Rolling a job back soft deletes exactly those questions (skipping any
 * edited since) and marks the job `rolled_back`, so a bad import can be undone and
 * run again
 * @format
 */

import type { Brand } from "./config/brands";

export const IMPORT_JOB_SOURCES = ["csv", "pdf"] as const;

export type ImportJobSource = (typeof IMPORT_JOB_SOURCES)[number];

/**
 * Lifecycle states of an import job.
 *
 * - `pending`: an import has claimed the job and is publishing rows.
 * - `running`: a PDF job whose extraction pipeline has started.
 * - `complete`: every row was published.
 * - `failed`: the import stopped at a row.
 * - `rolling_back`: a rollback has claimed the job and is publishing deletes.
 * - `rolled_back`: a rollback has finished.
 */
export const IMPORT_JOB_STATUSES = ["pending", "running", "complete", "failed", "rolling_back", "rolled_back"] as const;

export type ImportJobStatus = (typeof IMPORT_JOB_STATUSES)[number];

/**
 * S3 asset keys of the PDFs a PDF import reads, as returned by upload-asset.
 */
export interface ImportSourceKeys {
	/** Key of the question paper PDF */
	paper: string;
	/** Key of the mark scheme PDF */
	markScheme: string;
	/** Key of the specification PDF */
	specification: string;
}

/**
 * Progress counts of a PDF import, one number per pipeline stage.
 */
export interface ImportJobCounts {
	/** Questions the pipeline extracted from the PDFs */
	extracted: number;
	/** Extracted questions whose classification was accepted */
	accepted: number;
	/** Questions moved to `staged` */
	staged: number;
	/** Staged questions that have been published */
	published: number;
}

/**
 * A question a batch write refused to overwrite, because it is already staged or published.
 */
export interface ImportJobCollision {
	/** Id of the question the import tried to write */
	questionId: string;
	/** Status of the existing record, which blocked the overwrite */
	existingStatus: "staged" | "published";
}

/**
 * One persisted record per import.
 */
export interface ImportJob {
	/** UUID assigned when the import is received */
	id: string;
	brand: Brand;
	status: ImportJobStatus;
	/**
	 * Where the job's content came from. Absent on jobs recorded before PDF import
	 * existed, treat absent as `csv`.
	 */
	source?: ImportJobSource;
	/** S3 asset keys of the PDFs a PDF job reads. Absent on CSV jobs. */
	sourceKeys?: ImportSourceKeys;
	/**
	 * ARN of the Step Functions execution that extracts the PDFs. Set after the job
	 * is created, absent on CSV jobs.
	 */
	executionArn?: string;
	/**
	 * How far the extraction pipeline has got. Zeros when a PDF job is created and
	 * updated by the pipeline as it runs. Absent on CSV jobs.
	 */
	counts?: ImportJobCounts;
	/** Name of the uploaded CSV, when the client supplied it */
	fileName?: string;
	/**
	 * The Paper every question in the job belongs to. Optional so jobs recorded
	 * before it was added stay valid.
	 */
	paperId?: string;
	/**
	 * Subject of the job's paper, as shown on the import preview. A display hint
	 * for the history list.
	 */
	subject?: string;
	/**
	 * Commit hash of the classification prompt used for this job's inference. Metrics
	 * are attributed to it so accuracy can be traced to a prompt version.
	 */
	promptCommit?: string;
	/** Paper code of the job's paper, a display hint */
	paperCode?: string;
	/**Number of rows in the upload */
	rowCount: number;
	/** Identifier of the user that ran the import */
	createdBy: string;
	createdByEmail?: string;
	/** timestamp recording when the import was received */
	createdAt: string;
	/** timestamp recording when every row had been published */
	completedAt?: string;
	/**
	 * Why the job stopped early. Set when `status` is `failed`, or when a rollback
	 * stopped partway (the status is then left unchanged so the rollback can run again).
	 */
	failedReason?: string;
	/**
	 * Ids of the questions this job created, in row order. This is the audit trail
	 * a rollback works from.
	 */
	createdQuestionIds: string[];
	/** timestamp recording when a rollback claimed the job (`rolling_back`) or finished (`rolled_back`) */
	rolledBackAt?: string;
	/** Identifier of the user that rolled the job back. */
	rolledBackBy?: string;
	/**
	 * Questions a rollback left in place: edited or published since the import, or
	 * not yet written by the index worker. A non-empty list lets the rollback run again.
	 */
	rollbackSkippedIds?: string[];
	collisions?: ImportJobCollision[];
}

/**
 * The fields the extraction pipeline may update on an existing job. Every field
 * is optional, only the ones present are written.
 *
 * `createdQuestionIds` is the full list of questions the pipeline has created so
 * far, not an addition to the stored list: a write replaces what is stored. It is the
 * audit trail a rollback works from, with the same meaning as on a CSV job.
 */
export type ImportJobProgress = Partial<
	Pick<
		ImportJob,
		"status" | "executionArn" | "counts" | "createdQuestionIds" | "collisions" | "failedReason" | "completedAt"
	>
>;

/**
 * How long a CSV job may sit `pending` before it is treated as abandoned. Four
 * times the create Lambda's 30 s timeout, so a genuinely in-flight import
 * can never be rolled back underneath itself.
 */
export const STALE_PENDING_MS = 120_000;

/**
 * Decides whether an import job may be rolled back. The rollback endpoint
 * and the admin details page both apply this rule, so it lives here to keep the two
 * from drifting.
 *
 * A failed import may have created some questions, so it can be rolled
 * back too. A pending CSV job normally has an import still running, but if its Lambda
 * timed out mid-loop it never leaves `pending`, and its ids were persisted row
 * by row, so once stale it is undone like a failed job. A pending PDF job is only
 * waiting for its pipeline to start, so it is never rolled back. A job without a
 * `source` is a CSV job.
 *
 * A running job has its pipeline in progress and is never rolled back.
 *
 * A rolling-back job has a rollback in flight. If that rollback's Lambda timed out,
 * the job never leaves `rolling_back`, so once the claim is older than
 * {@link STALE_PENDING_MS} it can be rolled back again. The claim time is
 * `rolledBackAt`, not `createdAt`: the job may be old, but the claim is not.
 *
 * A rolled-back job that kept some questions can be rolled back again. A kept
 * question may only have been not yet written when the first rollback ran.
 *
 * Complete, failed and rolled-back jobs follow the same rule for both sources.
 *
 * @param job - The import job to check.
 * @param now - Current time in epoch milliseconds. Defaults to `Date.now()`.
 * @returns `true` for:
 * - a complete or failed job;
 * - a pending CSV job older than {@link STALE_PENDING_MS};
 * - a rolling-back job whose claim is older than {@link STALE_PENDING_MS};
 * - a rolled-back job with kept questions.
 *
 * A pending PDF job and a running job return `false`.
 */
export const canRollBackImportJob = (job: ImportJob, now = Date.now()): boolean => {
	if (job.status === "pending") {
		const source = job.source ?? "csv";
		return source === "csv" && now - Date.parse(job.createdAt) > STALE_PENDING_MS;
	}
	if (job.status === "rolling_back") {
		return now - Date.parse(job.rolledBackAt ?? job.createdAt) > STALE_PENDING_MS;
	}
	if (job.status === "rolled_back") {
		return (job.rollbackSkippedIds?.length ?? 0) > 0;
	}
	return job.status === "complete" || job.status === "failed";
};
